import { useState, useEffect } from "react";
import { Chart, useChart } from "@chakra-ui/charts";
import { Bar, BarChart, CartesianGrid, XAxis, YAxis, Tooltip, Cell } from "recharts";
import api from "../../../lib/api";

type CategoryData = {
  category: string;
  transactions: number;
  revenue: number;
  quantity: number;
};

interface TopSellingCategoriesProps {
  dateFilter: string;
  customRange?: { start: string; end: string };
  stateFilter?: string;
}

export const TopSellingCategories = ({ dateFilter, customRange, stateFilter }: TopSellingCategoriesProps) => {
  const [data, setData] = useState<CategoryData[]>([]);
  const [loading, setLoading] = useState(false);

  // Format currency with compact notation for y-axis
  const formatCompactCurrency = (value: number) => {
    return new Intl.NumberFormat("en-NG", {
      style: "currency",
      currency: "NGN",
      notation: "compact",
      maximumFractionDigits: 1,
    }).format(value);
  };

  // Helper to sync with Dashboard filters
  const getDates = (filter: string) => {
    if (filter === "custom" && customRange?.start && customRange?.end) {
        return { start_date: customRange.start, end_date: customRange.end };
    }

    const now = new Date();
    const end = new Date();
    let start = new Date();

    switch (filter) {
      case "24h":
        start.setDate(now.getDate() - 1);
        break;
      case "7d":
        start.setDate(now.getDate() - 7);
        break;
      case "30d":
        start.setDate(now.getDate() - 30);
        break;
      case "90d":
        start.setDate(now.getDate() - 90);
        break;
      case "180d":
        start.setDate(now.getDate() - 180);
        break;
      default:
        start.setDate(now.getDate() - 30);
    }
    return {
      start_date: start.toISOString().split("T")[0],
      end_date: end.toISOString().split("T")[0],
    };
  };

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const { start_date, end_date } = getDates(dateFilter);
        const params: any = { start_date, end_date, limit: 5 };
        if (stateFilter && stateFilter.trim() !== "") params.state = stateFilter;

        const response = await api.get("/analytics/categories/top-selling", {
          params,
        });
        
        if (response.data?.data) {
          setData(response.data.data);
        } else {
            setData([]);
        }
      } catch (error) {
        console.error("Failed to fetch top categories:", error);
        setData([]);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [dateFilter, customRange?.start, customRange?.end, stateFilter]);

  const chart = useChart({
    data: data,
    series: [{ name: "revenue", color: "purple.solid" }],
  });

  return (
    <div className="bg-white p-4! rounded-2xl shadow">
      <div className="flex items-center justify-between mb-4!">
        <p className="text-sm! font-semibold!">Top 5 Selling Categories</p>
      </div>

      <div>
        {!loading && data.length === 0 ? (
          <div className="flex h-[280px] w-full items-center justify-center text-gray-500 text-sm italic border border-dashed border-gray-200 rounded-lg">
            No category data available for this period
          </div>
        ) : (
          <Chart.Root height="280px" width="100%" chart={chart}>
            <BarChart data={data} barSize={40}>
              <CartesianGrid
                stroke={chart.color("border.muted")}
                vertical={false}
              />
              <XAxis
                axisLine={false}
                tickLine={false}
                dataKey="category"
                fontSize={10}
                interval={0}
                tickFormatter={(val) => val.length > 10 ? `${val.substring(0, 10)}...` : val}
              />
              <YAxis
                axisLine={false}
                tickLine={false}
                tickFormatter={(value) => formatCompactCurrency(value)}
              />
              <Tooltip
                cursor={{ fill: 'transparent' }}
                content={({ payload }) => {
                  if (!payload || payload.length === 0) return null;
                  const item = payload[0].payload;
                  return (
                    <div className="bg-white p-2 border rounded shadow text-sm">
                      <div className="font-bold">{item.category}</div>
                      <div>Revenue: ₦{item.revenue.toLocaleString()}</div>
                      <div>Txns: {item.transactions.toLocaleString()}</div>
                      <div>Qty: {item.quantity.toLocaleString()}</div>
                    </div>
                  );
                }}
              />
              <Bar
                dataKey="revenue"
                radius={[10, 10, 0, 0]}
                isAnimationActive={true}
              >
                 {data.map((_, index) => (
                    <Cell key={`cell-${index}`} fill="#805AD5" />
                 ))}
              </Bar>
            </BarChart>
          </Chart.Root>
        )}
      </div>
    </div>
  );
};
